// İSG uzmanı portalı — İzne gerekli eğitim ekleme (Modal + çoklu seçim). api-contract §17.2
// (`POST /permits/{uuid}/trainings {trainingUuids}` — **replace**). Seçenekler firmanın
// **aktif** eğitimlerinden (§16 `GET /trainings?isActive=true`) doldurulur; zaten gerekli
// olan eğitimler çıkarılır. Ekleme = mevcut gerekli set + yeni seçilenlerin **birleşimi**
// gönderilir (replace semantiği; mevcut korunur). Eğitim aktif/aynı firma değilse → BE
// `404 TRAINING_NOT_FOUND` (kısmi atama yok).

import {
  Alert,
  Button,
  EmptyState,
  Modal,
  type RequiredTrainingDto,
  Spinner,
  permitsApi,
  resolveApiErrorMessage,
  trainingApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { permitsKeys } from '../../lib/permitsKeys';

/** Aktif eğitim seçicisini tek sayfada besleyecek makul üst sınır (tenant ölçeği). */
const OPTIONS_PAGE_SIZE = 200;

interface RequiredTrainingAddModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permitUuid: string;
  /** Halihazırda gerekli eğitimler — replace birleşiminde korunur, seçenekten çıkarılır. */
  current: RequiredTrainingDto[];
}

export function RequiredTrainingAddModal({
  open,
  onOpenChange,
  permitUuid,
  current,
}: RequiredTrainingAddModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const trainingsQuery = useQuery({
    queryKey: ['permits', 'options', 'trainings'],
    queryFn: () =>
      trainingApi.listTrainings({ isActive: true, page: 1, pageSize: OPTIONS_PAGE_SIZE }),
    enabled: open,
    staleTime: 60_000,
  });

  const currentUuids = useMemo(() => current.map((c) => c.trainingUuid), [current]);
  const alreadyRequired = useMemo(() => new Set(currentUuids), [currentUuids]);
  const options = useMemo(
    () => (trainingsQuery.data?.items ?? []).filter((tr) => !alreadyRequired.has(tr.uuid)),
    [trainingsQuery.data, alreadyRequired],
  );

  const mutation = useMutation({
    mutationFn: () =>
      // Replace: mevcut gerekli set + yeni seçilenlerin birleşimi (mevcut korunur).
      permitsApi.setPermitTrainings(permitUuid, {
        trainingUuids: [...currentUuids, ...selected],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permitUuid) });
      setSelected(new Set());
      onOpenChange(false);
    },
  });

  function toggle(uuid: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }

  const noOptions = trainingsQuery.isSuccess && options.length === 0;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          setSelected(new Set());
        }
        onOpenChange(next);
      }}
      title={t('permits.trainings.add.title')}
      description={t('permits.trainings.add.description')}
      size="md"
      closeLabel={t('permits.common.close')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('permits.common.cancel')}
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={selected.size === 0 || noOptions}
          >
            {mutation.isPending ? (
              <Spinner label={t('permits.common.add')} />
            ) : (
              t('permits.common.add')
            )}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? (
          <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
        ) : null}

        {trainingsQuery.isPending ? (
          <Spinner label={t('permits.common.loading')} />
        ) : noOptions ? (
          <EmptyState variant="panel" title={t('permits.trainings.add.empty')} />
        ) : (
          <div className="flex max-h-72 flex-col gap-1 overflow-y-auto rounded-md border border-border p-2">
            {options.map((tr) => (
              <label
                key={tr.uuid}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg hover:bg-surface-muted"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={selected.has(tr.uuid)}
                  onChange={() => toggle(tr.uuid)}
                />
                <span className="font-medium">{tr.title}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
